// @flow
import { List, Record } from "immutable"
import type Booking from "@kiwicom/frontend-app/scripts/containers/mutable/Booking"

import { bookedAtSelector } from "common/services/branding/selectors"
import type { Currency, FlightId, PassengerId, PriceTotal } from "mmb/types/primitiveTypes"
import type { additionalServicesRequestForm } from "mmb/services/bookings/bookingsApi"
import {
  additionalServicesRequest,
  additionalServicesSeatingRequest,
} from "mmb/services/bookings/bookingsApi"
import { mapperCreatedBookingChange } from "../mapper/services/mapperManageBookingChange"
import type { Service } from "mmb/consts/additionalServices"
import { SERVICES } from "mmb/consts/additionalServices"
import { bidSelector } from "../selector"
import type { seatingApiPayload } from "../seating/helpers"
import type { SubmitForPaymentActionPayload } from "../../scenes/BookingPayment/records/PropTypes"
import type {
  Action,
  AdditionalServicesStateInst,
  AdditionalServicesStateType,
  GetState,
  ResetAction,
  SetCategoryAction,
} from "./types"
import {
  RESET,
  SET_CATEGORY,
  SUBMIT,
  SUBMIT_FAILURE,
  SUBMIT_REQUEST,
  SUBMIT_SEATS,
  SUBMIT_SUCCESS,
} from "mmb/scenes/AccountBooking/components/Booking/services/additionalServices/consts"

// eslint-disable-next-line import/no-cycle
import { reloadBooking } from ".."

const InitialState: AdditionalServicesStateType = Record({
  category: SERVICES.SEATING,
  success: false,
  loading: false,
  error: null,
  submitted: new List(),
})

export default function(state: ?AdditionalServicesStateInst = new InitialState(), action: Action) {
  if (!(state instanceof InitialState)) return new InitialState(state)

  switch (action.type) {
    case SET_CATEGORY:
      return state.set("category", action.category)

    case RESET:
      // $FlowFixMe
      return new InitialState({
        submitted: state.submitted, // we want to preserve history
      })

    case SUBMIT_REQUEST:
      return state
        .set("success", false)
        .set("loading", true)
        .set("error", null)

    case SUBMIT_SUCCESS:
      return (
        state
          // $FlowFixMe don't worry flow, everything's all right
          .update("submitted", submitted => submitted.push(action.request))
          .set("success", true)
          .set("loading", false)
          .set("error", null)
      )

    case SUBMIT_FAILURE:
      return state
        .set("success", false)
        .set("loading", false)
        .set("error", String(action.error))

    default:
      return state
  }
}

export const setCategory = (category: Service): SetCategoryAction => ({
  type: SET_CATEGORY,
  category,
})

export const reset = (): ResetAction => ({
  type: RESET,
})

export const submit = (booking: Booking, payload: additionalServicesRequestForm) => ({
  type: SUBMIT,
  action: payload.category, // action added for dataDogMapper
  request: payload,
  promise: additionalServicesRequest(booking, payload),
  _track: { bid: booking.id, request: payload },
})

export type partialSeatingApiPayload = {
  category: "seating",
  segments: {
    [flightId: FlightId]: {
      [paxId: PassengerId]: string,
    },
  },
  amount: PriceTotal,
  currency: Currency,
  useBalance?: boolean,
}

type serviceForPaymentType = SubmitForPaymentActionPayload & {
  payload: partialSeatingApiPayload,
}

// TODO: Refactor after testing BE
export const submitSeats = (serviceForPayment: serviceForPaymentType) => async (
  dispatch: Dispatch<*>,
  getState: GetState,
) => {
  const state = getState()
  const bid = bidSelector(state)
  const bookedAt = bookedAtSelector(state)
  const { payload } = serviceForPayment

  const apiPayload: seatingApiPayload = { ...payload, booked_at: bookedAt }

  const promise = additionalServicesSeatingRequest(bid, apiPayload)

  const ok = dispatch({
    type: SUBMIT_SEATS,
    action: SUBMIT_SEATS, // action added for isPaymentErrorSelector
    payload,
    promise,
    _track: { bid, payload },
  })

  if (ok) {
    if (payload.amount <= 0) {
      dispatch(reloadBooking(serviceForPayment.bid))
      return ok
    }
    return promise.then(mapperCreatedBookingChange)
  }

  return ok
}
